import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatNumber } from '../lib/utils';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ArrowLeft, DollarSign, Users, Star, Plus, 
  Check, Loader2, Crown, Lock, Gift 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONETIZATION_THRESHOLD = 1000;

export default function CreatorDashboardPage() {
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [showTierModal, setShowTierModal] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  
  // Tier form
  const [tierName, setTierName] = useState('');
  const [tierPrice, setTierPrice] = useState('');
  const [tierBenefits, setTierBenefits] = useState('');
  const [creatingTier, setCreatingTier] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/creator/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      
      if (response.data.is_creator) {
        const subResponse = await axios.get(`${API}/subscribers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscribers(subResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch creator status:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleEnableCreator = async () => {
    setEnabling(true);
    try {
      await axios.post(`${API}/creator/enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Creator Mode activated!');
      fetchStatus();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erè';
      toast.error(message);
    } finally {
      setEnabling(false);
    }
  };

  const handleCreateTier = async () => {
    if (!tierName || !tierPrice) {
      toast.error('Ranpli tout chan yo');
      return;
    }
    
    setCreatingTier(true);
    try {
      await axios.post(`${API}/creator/tiers`, {
        name: tierName,
        price: parseFloat(tierPrice),
        benefits: tierBenefits.split('\n').filter(b => b.trim())
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Tier kreye!');
      setShowTierModal(false);
      setTierName('');
      setTierPrice('');
      setTierBenefits('');
      fetchStatus();
    } catch (error) {
      toast.error('Error creating tier');
    } finally {
      setCreatingTier(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const progress = Math.min((status?.followers_count / MONETIZATION_THRESHOLD) * 100, 100);

  return (
    <div className="max-w-4xl mx-auto pb-20" data-testid="creator-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Crown className="w-6 h-6 text-primary" />
            <h1 className="font-bold text-lg">Creator Dashboard</h1>
          </div>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {/* Eligibility Card */}
        {!status?.is_creator && (
          <Card className="border-2 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-6 h-6 text-primary" />
                Monetizasyon
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Progress</span>
                  <span className="font-semibold">
                    {formatNumber(status?.followers_count || 0)} / {formatNumber(MONETIZATION_THRESHOLD)} subscribers
                  </span>
                </div>
                <div className="h-3 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-pink-400 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              
              {status?.is_eligible ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-500">
                    <Check className="w-5 h-5" />
                    <span className="font-semibold">Ou elijib pou monetizasyon!</span>
                  </div>
                  <Button 
                    onClick={handleEnableCreator}
                    disabled={enabling}
                    className="w-full rounded-full"
                    data-testid="enable-creator-btn"
                  >
                    {enabling ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        Activate Creator Mode
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Lock className="w-5 h-5" />
                  <span>
                    You need {MONETIZATION_THRESHOLD - (status?.followers_count || 0)} more subscribers to activate monetization
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Creator Dashboard */}
        {status?.is_creator && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        ${status.creator_profile?.total_earnings?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-sm text-muted-foreground">Revni Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-pink-500/10 rounded-2xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-pink-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{subscribers.length}</p>
                      <p className="text-sm text-muted-foreground">Paid Subscribers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-yellow-500/10 rounded-2xl flex items-center justify-center">
                      <Gift className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {status.creator_profile?.tips_enabled ? 'Wi' : 'Non'}
                      </p>
                      <p className="text-sm text-muted-foreground">Tips Aktive</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-2xl flex items-center justify-center">
                      <Star className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {status.creator_profile?.subscription_tiers?.length || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Subscription Tiers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Subscription Tiers */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Subscription Tiers</CardTitle>
                <Button 
                  size="sm" 
                  className="rounded-full"
                  onClick={() => setShowTierModal(true)}
                  data-testid="add-tier-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Tier
                </Button>
              </CardHeader>
              <CardContent>
                {status.creator_profile?.subscription_tiers?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Star className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No tiers yet. Create your first tier!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {status.creator_profile?.subscription_tiers?.map((tier, index) => (
                      <div 
                        key={tier.id || index}
                        className="flex items-center justify-between p-4 bg-secondary/50 rounded-2xl"
                      >
                        <div>
                          <h4 className="font-semibold">{tier.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {tier.benefits?.length || 0} benefis
                          </p>
                        </div>
                        <div className="text-xl font-bold text-primary">
                          ${tier.price}/mwa
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Subscribers */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Subscribers</CardTitle>
              </CardHeader>
              <CardContent>
                {subscribers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No paid subscribers yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {subscribers.slice(0, 5).map(sub => (
                      <div 
                        key={sub.id}
                        className="flex items-center gap-3 p-3 bg-secondary/30 rounded-2xl"
                      >
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            @{sub.subscriber?.username || 'Unknown'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {sub.tier_name} · ${sub.price}/mwa
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Create Tier Modal */}
      <Dialog open={showTierModal} onOpenChange={setShowTierModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Tier</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tier Name</label>
              <Input
                placeholder="Ex: VIP, Premium, Basic..."
                value={tierName}
                onChange={(e) => setTierName(e.target.value)}
                className="rounded-2xl"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Monthly Price ($)</label>
              <Input
                type="number"
                placeholder="9.99"
                value={tierPrice}
                onChange={(e) => setTierPrice(e.target.value)}
                className="rounded-2xl"
                min="0.99"
                step="0.01"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Benefits (one per line)</label>
              <Textarea
                placeholder="Exclusive content access&#10;Direct messages&#10;Special badge"
                value={tierBenefits}
                onChange={(e) => setTierBenefits(e.target.value)}
                className="rounded-2xl resize-none"
                rows={4}
              />
            </div>
            
            <Button
              onClick={handleCreateTier}
              disabled={creatingTier || !tierName || !tierPrice}
              className="w-full rounded-full"
            >
              {creatingTier ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Tier
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
